import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Video, FileText, MessageCircle, Zap } from "lucide-react";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";

const features = [
  {
    icon: Calendar,
    title: "Smart Organization",
    description: "Digital binder with color-coded tabs: Discussion (purple), Assignments (amber), Experiential (green), and Live Sessions (cyan). All your coursework organized intuitively.",
  },
  {
    icon: Users,
    title: "Real-Time Collaboration",
    description: "See who's online, direct message classmates, create group chats, and coordinate study sessions. Built-in presence indicators show availability.",
  },
  {
    icon: Video,
    title: "Virtual Meeting Rooms",
    description: "Host video calls with up to 8 participants. Features include screen sharing, live AI transcription, virtual backgrounds, and persistent chat rooms.",
  },
  {
    icon: FileText,
    title: "Document Hub",
    description: "Upload PDFs, images, audio files. Create rich-text notes with formatting. Track assignments with due dates. Export journals to e-readers.",
  },
  {
    icon: MessageCircle,
    title: "Integrated Messaging",
    description: "Instant 1-on-1 and group messaging. Online status indicators. Message history persists across sessions. Mobile-friendly interface.",
  },
  {
    icon: Zap,
    title: "Calendar & Scheduling",
    description: "Visual calendar view of all deadlines and meetings. Schedule virtual room sessions. Import from Google Calendar. Set reminders and recurring events.",
  },
];

const OrganizerLanding: React.FC = () => {
  const [showLogo, setShowLogo] = useState(false);
  const [showWord1, setShowWord1] = useState(false);
  const [showWord2, setShowWord2] = useState(false);
  const [showWord3, setShowWord3] = useState(false);
  const [showWord4, setShowWord4] = useState(false);
  const [showWhipt, setShowWhipt] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Animation sequence
    const timer1 = setTimeout(() => setShowLogo(true), 500);
    const timer2 = setTimeout(() => setShowWord1(true), 2000);
    const timer3 = setTimeout(() => setShowWord2(true), 2300);
    const timer4 = setTimeout(() => setShowWord3(true), 2600);
    const timer5 = setTimeout(() => setShowWord4(true), 2900);
    const timer6 = setTimeout(() => setShowWhipt(true), 3400);
    const timer7 = setTimeout(() => setShowContent(true), 4200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
      clearTimeout(timer6);
      clearTimeout(timer7);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 space-y-20">
        
        {/* Hero Section with Cinematic Animation */}
        <header className="text-center space-y-8 pt-8 min-h-[70vh] flex flex-col items-center justify-center">
          <div className="space-y-6">
            
            {/* Pronoia Wellness Logo - Fade in from darkness */}
            <div 
              className={`inline-block transition-all duration-2000 ${
                showLogo ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="border-4 border-black rounded-lg p-6 bg-white/5 backdrop-blur-sm">
                <img
                  src="/pronoia-wellness-hero2.webp"
                  alt="Pronoia Wellness"
                  className="max-w-md w-auto object-contain"
                />
              </div>
            </div>

            {/* "Where'd I Put That?" - Slide in word by word */}
            <div className="text-center space-y-4 px-4">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold">
                <span 
                  className={`bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent transition-all duration-700 ${
                    showWord1 ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
                  }`}
                >
                  Where'd
                </span>
                <span 
                  className={`bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent transition-all duration-700 ${
                    showWord2 ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
                  }`}
                >
                  I
                </span>
                <span 
                  className={`bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent transition-all duration-700 ${
                    showWord3 ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
                  }`}
                >
                  Put
                </span>
                <span 
                  className={`bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent transition-all duration-700 ${
                    showWord4 ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
                  }`}
                >
                  That?
                </span>
              </div>

              {/* WHIPT - SLAM IN */}
              <div 
                className={`text-7xl sm:text-8xl md:text-9xl font-black transition-all duration-500 ${
                  showWhipt 
                    ? 'translate-y-0 opacity-100 scale-100' 
                    : 'translate-y-10 opacity-0 scale-150'
                }`}
              >
                <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 bg-clip-text text-transparent drop-shadow-2xl">
                  WHIPT
                </span>
              </div>

              {/* Subtitle */}
              <p 
                className={`text-sm sm:text-base md:text-lg uppercase tracking-[0.2em] sm:tracking-[0.3em] md:tracking-[0.4em] text-amber-200/70 font-semibold transition-all duration-1000 px-4 ${
                  showWhipt ? 'opacity-100' : 'opacity-0'
                }`}
              >
                Scholarly Student Organizer
              </p>
            </div>
          </div>
          
          {/* Description and CTA - Dissolve in */}
          <div 
            className={`transition-all duration-1500 ${
              showContent ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-300 leading-relaxed mb-8">
              The organizer built for students who juggle classes, group projects, and study sessions—all in one beautifully simple interface.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/organizer/sign-up">
                <button className="px-10 py-4 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold text-base uppercase tracking-[0.2em] shadow-[0_20px_60px_rgba(251,146,60,0.4)] hover:shadow-[0_20px_80px_rgba(251,146,60,0.6)] hover:scale-105 transition-all duration-300">
                Get Started
              </button>
            </Link>
            <Link to="/organizer/sign-in">
              <button className="px-10 py-4 rounded-full border-2 border-white/30 text-base uppercase tracking-[0.2em] text-white/90 hover:text-white hover:border-white/60 hover:bg-white/5 transition-all duration-300">
                Sign In
              </button>
            </Link>
          </div>

          <p className="text-xs text-slate-500 pt-4">
            Flexible subscription plans · Support education through contribution
          </p>
          </div>
        </header>

        {/* Features Grid - Fade in */}
        <section 
          className={`space-y-8 transition-all duration-1500 ${
            showContent ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Everything You Need</h2>
            <p className="text-slate-400">Built for the way students actually work</p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="group rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] p-6 backdrop-blur hover:border-amber-400/40 hover:shadow-[0_8px_32px_rgba(251,146,60,0.15)] transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400/20 to-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-amber-300" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-200 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA Section */}
        <section className="rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-amber-900/20 p-12 text-center backdrop-blur-sm">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to get organized?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join students who are finally keeping track of everything—classes, deadlines, study groups, and more.
          </p>
          <Link to="/organizer/sign-up">
            <button className="px-12 py-5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold text-lg uppercase tracking-[0.2em] shadow-[0_20px_60px_rgba(251,146,60,0.4)] hover:shadow-[0_20px_80px_rgba(251,146,60,0.6)] hover:scale-105 transition-all duration-300">
              Start Organizing Now
            </button>
          </Link>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-slate-400 space-y-4 pb-8">
          <div className="flex items-center justify-center gap-6">
            <a href="/terms" className="hover:text-amber-300 transition-colors">
              Terms of Service
            </a>
            <span className="text-slate-600">•</span>
            <a href="/privacy" className="hover:text-amber-300 transition-colors">
              Privacy Policy
            </a>
            <span className="text-slate-600">•</span>
            <a href="mailto:contact@theblackbettermentquest.org" className="hover:text-amber-300 transition-colors flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact
            </a>
            <span className="text-slate-600">•</span>
            <a href="mailto:support@theblackbettermentquest.org" className="hover:text-amber-300 transition-colors flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Support
            </a>
          </div>
          <p className="text-slate-500">Built for students, by students</p>
          <div className="text-xs text-slate-600 space-y-1">
            <p>© 2026 Monica L. James. All rights reserved.</p>
            <p>Where'd I Put That? (WHIPT) is the intellectual property of Monica L. James and Pronoia Wellness LLC.</p>
          </div>
        </footer>
      </div>

      {/* PWA Install Prompt */}
      <InstallPWAPrompt />
    </div>
  );
};

export default OrganizerLanding;
